import React, { useState, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Backdrop, Grow } from '@material-ui/core';
import DropzoneComponent from 'react-dropzone-component/dist/react-dropzone';
import Config from '../../commons/config/Config.js';
import * as Utils from '../../commons/utils/Utils.js';

const BASE_URL = '/inventario/tarifarios';

const PREVIEW_TEMPLATE = `
		        <div class="dz-preview">
		          <div class="dz-image">
		            <i class="fa fa-file-excel-o" style="font-size:80px"></i>
		          </div>
		         <div class="dz-success-mark">
		            <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:sketch="http://www.bohemiancoding.com/sketch/ns">      <title>Check</title>      <defs></defs>      <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketch:type="MSPage">        <path d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z" id="Oval-2" stroke-opacity="0.198794158" stroke="#747474" fill-opacity="0.816519475" fill="#FFFFFF" sketch:type="MSShapeGroup"></path>      </g>    </svg>
		          </div>
		          <div class="dz-error-mark">
		            <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:sketch="http://www.bohemiancoding.com/sketch/ns">      <title>Error</title>      <defs></defs>      <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketch:type="MSPage">        <g id="Check-+-Oval-2" sketch:type="MSLayerGroup" stroke="#747474" stroke-opacity="0.198794158" fill="#FFFFFF" fill-opacity="0.816519475">          <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z" id="Oval-2" sketch:type="MSShapeGroup"></path>        </g>      </g>    </svg>
		          </div>
		          <div class="dz-error-message"><span data-dz-errormessage></span></div>
		        </div>
		    `;

/**
 * VEC-3499 Scenario 06 — importación de los ítems de un tarifario, con el estilo del importador de
 * móviles y combustibles: popup con el botón de la plantilla y el Dropzone, y un segundo popup con el
 * reporte de lo que pasó.
 *
 * El PRIMER import tiene que traer TODOS los ítems del Catálogo (activos e inactivos): la plantilla
 * del tarifario ya los trae con los valores actuales. Los siguientes pueden ser parciales. Solo se
 * guardan Costo, Valor máximo y Costo fijo, también de los inactivos. Todo o nada: una sola fila
 * errónea vuelve en el reporte (y en un Excel descargable) y no se importa ninguna. Si faltan ítems
 * en el primer import se rechaza el archivo y el popup lista los que faltan.
 *
 * Props: open, tarifarioId, onClose, onImportado (se llama cuando el import modificó algún ítem).
 */
const InventarioTarifariosImportModal = (props) => {
  const intl = useIntl();

  const [archivo, setArchivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [descargandoPlantilla, setDescargandoPlantilla] = useState(false);
  const [descargandoErrores, setDescargandoErrores] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);

  const dropzoneRef = useRef(null);
  // Los handlers del Dropzone se crean una sola vez: leen las props vigentes desde acá.
  const propsRef = useRef(props);
  propsRef.current = props;

  const descargarBlob = (filename, data) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    // dataBlob devuelve el filename tal como viene del header, con las comillas incluidas.
    link.setAttribute('download', String(filename).replace(/"/g, ''));
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /** Los motivos son claves i18n con su context en `motivosContext`, alineado por índice. */
  const traducirMotivo = (error, motivo, i) =>
    Utils.translateBackendKey(motivo, (error.motivosContext || [])[i] || null, intl);

  /** Forma {mensaje, context} de BadRequestException, igual que el import del Catálogo. */
  const mensajeDeError = (data) => {
    if (data && data.codigo === 4030) {
      return intl.formatMessage({
        id: 'inventarioTarifarios.import.sin_permiso',
        defaultMessage: 'No tiene permiso para realizar esta acción.',
      });
    }
    if (data && data.mensaje) {
      return Utils.translateBackendKey(data.mensaje, data.context || null, intl);
    }
    if (typeof data === 'string' && data !== '') {
      return data;
    }

    return intl.formatMessage({
      id: 'inventarioTarifarios.import.revise_el_formato',
      defaultMessage: 'Revise que el archivo tenga el formato de la plantilla del tarifario.',
    });
  };

  const handleDescargarPlantilla = () => {
    setDescargandoPlantilla(true);
    Utils.dataBlob(`${BASE_URL}/${props.tarifarioId}/items/plantilla`, 'GET', {})
      .then((res) => descargarBlob(res.filename, res.data))
      // Utils.dataBlob ya avisa el error por Swal; el catch solo deja correr el finally.
      .catch(() => {})
      .finally(() => setDescargandoPlantilla(false));
  };

  const handleDescargarErrores = () => {
    if (!resultado || !resultado.errores || resultado.errores.length === 0) return;
    setDescargandoErrores(true);
    // Se traduce acá: el Excel lo arma el backend, que no tiene el catálogo de i18n.
    const errores = resultado.errores.map((error) => ({
      ...error,
      motivos: (error.motivos || []).map((motivo, i) => traducirMotivo(error, motivo, i)),
    }));
    Utils.dataBlob(`${BASE_URL}/items/exportar-errores-importacion`, 'POST', { errores })
      .then((res) => descargarBlob(res.filename, res.data))
      .catch(() => {})
      .finally(() => setDescargandoErrores(false));
  };

  const dropzoneConfig = useMemo(
    () => ({
      showFiletypeIcon: false,
      postUrl: Config.get('apiUrlBase') + `${BASE_URL}/${props.tarifarioId}/items/importar`,
    }),
    [props.tarifarioId]
  );

  const dropzoneDjsConfig = useMemo(
    () => ({
      maxFiles: 2,
      addRemoveLinks: false,
      thumbnailMethod: 'crop',
      dictDefaultMessage:
        intl.formatMessage({ id: 'inventarioTarifarios.import.haga_click_aqui_o', defaultMessage: 'Haga click aquí o' }) +
        '<br>' +
        intl.formatMessage({ id: 'inventarioTarifarios.import.arrastre_un_archivo', defaultMessage: 'arrastre un archivo a este área' }) +
        '<br>' +
        intl.formatMessage({ id: 'inventarioTarifarios.import.para_cargar_los_datos', defaultMessage: 'para cargar los datos.' }),
      dictInvalidFileType: intl.formatMessage({
        id: 'inventarioTarifarios.import.tipo_archivo_incorrecto',
        defaultMessage: 'El tipo de archivo es incorrecto. Solo se permiten .xlsx, .xls y .csv',
      }),
      dictResponseError: 'Error interno con código {{statusCode}}.',
      dictMaxFilesExceeded: intl.formatMessage({
        id: 'inventarioTarifarios.import.un_archivo_por_vez',
        defaultMessage: 'Solo se puede procesar un archivo a la vez.',
      }),
      dictRemoveFileConfirmation: null,
      uploadMultiple: false,
      parallelUploads: 1,
      acceptedFiles: '.xlsx,.xls,.csv',
      autoProcessQueue: false,
      timeout: 12000000,
      headers: {
        'Authorization-Token': localStorage.getItem('token'),
      },
      previewTemplate: PREVIEW_TEMPLATE,
      init: function() {
        const dropzone = this;

        this.on('success', (file, response) => {
          dropzone.removeFile(file);
          const rta = typeof response === 'string' ? JSON.parse(response) : response;
          setImportando(false);
          setArchivo(null);
          setResultado(rta);
          if (rta && rta.actualizadas > 0 && propsRef.current.onImportado) {
            propsRef.current.onImportado();
          }
        });

        // Un error de todo el archivo (faltan ítems, columnas obligatorias, formato) se muestra en el
        // mismo popup para corregir y volver a intentar.
        this.on('error', (file, errormessage) => {
          dropzone.removeFile(file);
          setImportando(false);
          setArchivo(null);
          setErrorArchivo(mensajeDeError(errormessage));
        });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.tarifarioId]
  );

  const dropzoneEventHandlers = {
    init: (dz) => {
      dropzoneRef.current = dz;
    },
    addedfile: (file) => {
      const dz = dropzoneRef.current;
      if (dz && dz.files.length > 1) {
        dz.removeFile(dz.files[0]);
      }
      setArchivo(file);
      setErrorArchivo(null);
    },
  };

  const handleImportar = (event) => {
    event.preventDefault();
    if (!archivo || !dropzoneRef.current) return;
    setErrorArchivo(null);
    setImportando(true);
    dropzoneRef.current.processQueue();
  };

  const handleCancelar = () => {
    setArchivo(null);
    setErrorArchivo(null);
    props.onClose();
  };

  const handleCerrarReporte = () => setResultado(null);

  const hayErrores = resultado && resultado.errores && resultado.errores.length > 0;
  const entroAlguna = resultado && resultado.actualizadas > 0;

  return (
    <>
      <Modal
        open={props.open && !resultado}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 1000 }}
      >
        <Grow in={props.open && !resultado}>
          <div className="col-8" tabIndex="-1" role="dialog" style={{ margin: 'auto' }}>
            <div className="modal-xl modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header bg-fleet">
                  <h4 className="modal-title text-white">
                    <FormattedMessage id="inventarioTarifarios.import.titulo" defaultMessage="Importar ítems al tarifario" />
                  </h4>
                  <button type="button" className="close" onClick={handleCancelar} aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body modal-grey" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                  <form className="form">
                    <div className="card pull-up">
                      <div className="card-content pl-1 pr-1">
                        <div className="card-body">
                          <h4 className="form-section">
                            <i className="la la-file" />{' '}
                            <FormattedMessage id="inventarioTarifarios.import.descargar_plantilla_titulo" defaultMessage="Descargar plantilla" />
                          </h4>
                          <div className="small text-muted mb-1">
                            <FormattedMessage
                              id="inventarioTarifarios.import.ayuda"
                              defaultMessage="La plantilla trae todos los ítems del Catálogo (activos e inactivos) con los valores actuales de este tarifario. La primera importación tiene que incluir todos los ítems: si falta alguno no se importa nada. Las siguientes pueden traer solo los ítems a modificar. Solo se guardan Costo, Valor máximo y Costo fijo; un Costo o Valor máximo vacío o en 0 usa el del Catálogo, y un Costo fijo vacío es No. Los ítems inactivos se importan igual que los activos. Si alguna fila tiene un error no se importa ninguna."
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary mr-1 mb-1 col-5"
                            disabled={descargandoPlantilla}
                            onClick={handleDescargarPlantilla}
                          >
                            <i className={descargandoPlantilla ? 'la la-spinner spinner mr-1' : 'fa fa-download mr-1'} />
                            <FormattedMessage id="inventarioTarifarios.import.descargar_plantilla" defaultMessage="Descargar plantilla" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card pull-up">
                      <div className="card-content pl-1 pr-1">
                        <div className="card-body">
                          <h4 className="form-section">
                            <i className="la la-file" />{' '}
                            <FormattedMessage id="inventarioTarifarios.import.importar_archivo" defaultMessage="Importar archivo" />
                          </h4>

                          {errorArchivo && (
                            <div className="alert alert-danger" role="alert">
                              <strong>
                                <FormattedMessage id="inventarioTarifarios.import.error_al_importar" defaultMessage="No se pudo procesar el archivo" />
                              </strong>
                              {/* pre-line: el mensaje de "faltan ítems" trae un ítem por línea. */}
                              <div style={{ whiteSpace: 'pre-line', maxHeight: 240, overflowY: 'auto' }}>{errorArchivo}</div>
                            </div>
                          )}

                          <div className="row mt-1">
                            <div className="col-md-12">
                              <DropzoneComponent
                                id="archivos"
                                config={dropzoneConfig}
                                eventHandlers={dropzoneEventHandlers}
                                djsConfig={dropzoneDjsConfig}
                              />
                            </div>
                          </div>
                          <hr />
                          <div className="row mt-1">
                            <div className="col-md-12">
                              {archivo ? (
                                <table className="table">
                                  <tbody>
                                    <tr>
                                      <td className="align-middle">{archivo.name}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              ) : (
                                <div>
                                  <FormattedMessage id="inventarioTarifarios.import.no_hay_archivos_cargados" defaultMessage="No hay archivos cargados." />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="alert alert-info small mt-1 mb-0" role="alert">
                            <FormattedMessage id="inventarioTarifarios.import.puede_demorar" defaultMessage="Esta acción puede demorar unos minutos." />
                          </div>
                        </div>
                        <div className="card-footer">
                          <div className="text-right">
                            <button type="submit" className="btn btn-primary mr-1" disabled={!archivo || importando} onClick={handleImportar}>
                              <i className={importando ? 'la la-spinner spinner' : 'fa fa-check-circle'} />
                              <FormattedMessage id="inventarioTarifarios.import.importar" defaultMessage=" Importar" />
                            </button>
                            <button type="button" className="btn btn-danger" disabled={importando} onClick={handleCancelar}>
                              <i className="fa fa-times-circle" />
                              <FormattedMessage id="inventarioTarifarios.import.cancelar" defaultMessage=" Cancelar" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </Grow>
      </Modal>

      {/* Reporte de la importación: lo que se importó y el detalle de las filas que no entraron. */}
      <Modal open={!!resultado} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 1000 }}>
        <Grow in={!!resultado}>
          <div className="col-12" tabIndex="-1" role="dialog" style={{ margin: 'auto' }}>
            <div className="modal-lg modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header bg-fleet">
                  <h4 className="modal-title text-white">
                    <FormattedMessage id="inventarioTarifarios.import.reporte_titulo" defaultMessage="Reporte de la importación" />
                  </h4>
                  <button type="button" className="close" onClick={() => { handleCerrarReporte(); props.onClose(); }} aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body modal-grey modal-mh ovf-x-hidden" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {resultado && (
                    <div className="card pull-up">
                      <div className="card-content pl-1 pr-1">
                        <div className="card-body">
                          <div className={'alert mb-2 ' + (!hayErrores ? 'alert-success' : entroAlguna ? 'alert-warning' : 'alert-danger')}>
                            <strong>
                              {!hayErrores && entroAlguna && (
                                <FormattedMessage id="inventarioTarifarios.import.exito" defaultMessage="Importación finalizada" />
                              )}
                              {!hayErrores && !entroAlguna && (
                                <FormattedMessage
                                  id="inventarioTarifarios.import.sin_cambios"
                                  defaultMessage="Importación finalizada: el archivo no cambió ningún ítem"
                                />
                              )}
                              {/* Todo o nada: con una sola fila errónea el backend no escribe ninguna. */}
                              {hayErrores && (
                                <FormattedMessage
                                  id="inventarioTarifarios.import.rechazado_por_errores"
                                  defaultMessage="No se importó ningún ítem. Corregí las filas marcadas y volvé a subir el archivo."
                                />
                              )}
                            </strong>
                          </div>

                          <div className="d-flex flex-wrap" style={{ gap: 24 }}>
                            <div>
                              <strong>
                                <FormattedMessage id="inventarioTarifarios.import.procesadas" defaultMessage="Filas procesadas" />:
                              </strong>{' '}
                              {resultado.procesadas}
                            </div>
                            <div className="text-success">
                              <strong>
                                <FormattedMessage id="inventarioTarifarios.import.actualizadas" defaultMessage="Ítems actualizados" />:
                              </strong>{' '}
                              {resultado.actualizadas}
                            </div>
                            <div className={resultado.conError > 0 ? 'text-danger' : ''}>
                              <strong>
                                <FormattedMessage id="inventarioTarifarios.import.con_error" defaultMessage="Con error" />:
                              </strong>{' '}
                              {resultado.conError}
                            </div>
                          </div>

                          {hayErrores && (
                            <div className="mt-2">
                              <h4 className="form-section">
                                <FormattedMessage id="inventarioTarifarios.import.detalle_errores" defaultMessage="Filas que fallaron" />
                              </h4>
                              <div className="table-responsive">
                                <table className="table table-sm table-bordered table-hover mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ width: 70 }}>
                                        <FormattedMessage id="inventarioTarifarios.import.columna_fila" defaultMessage="Fila" />
                                      </th>
                                      <th style={{ width: 90 }}>
                                        <FormattedMessage id="inventarioTarifarios.import.columna_id" defaultMessage="ID" />
                                      </th>
                                      <th style={{ width: 220 }}>
                                        <FormattedMessage id="inventarioTarifarios.import.columna_descripcion" defaultMessage="Descripción" />
                                      </th>
                                      <th>
                                        <FormattedMessage id="inventarioTarifarios.import.columna_motivos" defaultMessage="Motivos" />
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {resultado.errores.map((error, indice) => (
                                      <tr key={'error_' + indice}>
                                        <td>{error.fila}</td>
                                        <td>{error.itemId || '-'}</td>
                                        <td>{error.descripcion || '-'}</td>
                                        <td>
                                          <ul className="mb-0 pl-3">
                                            {(error.motivos || []).map((motivo, i) => (
                                              <li key={'motivo_' + indice + '_' + i}>{traducirMotivo(error, motivo, i)}</li>
                                            ))}
                                          </ul>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer modal-grey">
                  {hayErrores && (
                    <button type="button" className="btn btn-primary btn-fleet" disabled={descargandoErrores} onClick={handleDescargarErrores}>
                      <i className={descargandoErrores ? 'la la-spinner spinner mr-1' : 'fa fa-download mr-1'} />
                      <FormattedMessage id="inventarioTarifarios.import.descargar_errores" defaultMessage="Descargar filas con error" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-default btn-fleet"
                    onClick={() => {
                      handleCerrarReporte();
                      props.onClose();
                    }}
                  >
                    <FormattedMessage id="inventarioTarifarios.import.aceptar" defaultMessage="Aceptar" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Grow>
      </Modal>
    </>
  );
};

export default InventarioTarifariosImportModal;
